import { useState, useEffect } from "react";
import { Moon, Sun, Monitor } from "lucide-react";
import useDarkSide from "./useDarkSide";

export default function Switcher() {
  const [colorTheme, setTheme] = useDarkSide();
  const [currentMode, setCurrentMode] = useState("light");
  const [showTooltip, setShowTooltip] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const modes = ["light", "dark", "system"];

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (
      savedTheme === "dark" ||
      savedTheme === "light" ||
      savedTheme === "system"
    ) {
      setCurrentMode(savedTheme);
    } else {
      setCurrentMode("light");
    }
  }, []);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (
      savedTheme === "dark" ||
      savedTheme === "light" ||
      savedTheme === "system"
    ) {
      setCurrentMode(savedTheme);
    }
  }, [colorTheme]);

  const toggleMode = () => {
    if (isAnimating) return;

    setIsAnimating(true);
    const currentIndex = modes.indexOf(currentMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    const nextMode = modes[nextIndex];

    setCurrentMode(nextMode);
    setTheme(nextMode);
    setShowTooltip(false);

    setTimeout(() => {
      setIsAnimating(false);
    }, 300);
  };

  const getTooltipText = (mode) => {
    switch (mode) {
      case "dark":
        return "Dark";
      case "light":
        return "Light";
      case "system":
        return "System";
      default:
        return "System";
    }
  };

  const handlePointerEnter = (event) => {
    if (event.pointerType === "mouse") setShowTooltip(true);
  };

  const handlePointerLeave = () => {
    setShowTooltip(false);
  };

  const renderIcon = () => {
    const baseClasses =
      "transition-all duration-300 ease-in-out dark:text-[#707070]";

    switch (currentMode) {
      case "dark":
        return (
          <div
            className={`${baseClasses} transform rotate-0 scale-100 ${
              isAnimating ? "animate-dark-mode" : ""
            }`}
          >
            <Moon size={20} />
          </div>
        );
      case "light":
        return (
          <div
            className={`${baseClasses} transform rotate-0 scale-100 ${
              isAnimating ? "animate-light-mode" : ""
            }`}
          >
            <Sun size={20} />
          </div>
        );
      case "system":
        return (
          <div
            className={`${baseClasses} transform scale-100 ${
              isAnimating ? "animate-system-mode" : ""
            }`}
          >
            <Monitor size={20} />
          </div>
        );
      default:
        return (
          <div
            className={`${baseClasses} transform scale-100 ${
              isAnimating ? "animate-system-mode" : ""
            }`}
          >
            <Monitor size={20} />
          </div>
        );
    }
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={toggleMode}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        className={`
          p-2 rounded-md
          hover:bg-gray-200 dark:hover:bg-gray-700
          bg-transparent cursor-pointer
          ${isAnimating ? "animate-button-press" : ""}
        `}
        aria-label={`Switch theme. Current mode: ${currentMode}`}
      >
        {renderIcon()}
      </button>

      {showTooltip && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-10">
          <div className="relative bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap transition-all duration-200 animate-fade-in">
            {getTooltipText(currentMode)}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-gray-900"></div>
          </div>
        </div>
      )}

      {/* Custom styles matching react-toggle-dark-mode animation */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Moon animation - slower */
        @keyframes dark-mode-animation {
          0% {
            transform: scale(0.9) rotate(-40deg);
            opacity: 0.8;
          }
          60% {
            transform: scale(1.05) rotate(-10deg);
            opacity: 0.9;
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }

        /* Sun animation - slower rotating left to right */
        /* Sun animation - extra slow rotating left to right */
        @keyframes light-mode-animation {
          0% {
            transform: scale(0.95) rotate(-30deg);
            opacity: 0.85;
          }
          70% {
            transform: scale(1.02) rotate(-5deg);
            opacity: 0.95;
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }
        /* System mode animation - slower fade and scale */
        @keyframes system-mode-animation {
          0% {
            transform: scale(0.9);
            opacity: 0.8;
          }
          60% {
            transform: scale(1.05);
            opacity: 0.9;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        /* Button press effect */
        @keyframes button-press {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(0.95);
          }
          100% {
            transform: scale(1);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }

        .animate-dark-mode {
          animation: dark-mode-animation 0.3s ease-in-out;
        }

        .animate-light-mode {
          animation: light-mode-animation 0.3s ease-in-out;
        }

        .animate-system-mode {
          animation: system-mode-animation 0.3s ease-in-out;
        }

        .animate-button-press {
          animation: button-press 0.3s ease-in-out;
        }
      `}</style>
    </div>
  );
}
